import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import {
  getPortalDocuments,
  getPortalMe,
  portalAcceptQuote,
  portalRejectQuote,
} from "../../lib/api";
import { formatPriceCents } from "../jobs/lib/jobForm";
import type { PortalInvoice, PortalProfile, PortalQuote } from "./types";
import styles from "./PortalPage.module.css";

/**
 * Client portal (Phase B, US-CDD-006).
 *
 * Self-service surface for a linked client user: every sent/accepted quote and
 * sent invoice for their organisation, with PDF download and quote
 * accept/reject. Scoping is server-side (client_portal_users); this page only
 * renders what the API returns. Deliberately outside the vendor AppShell.
 */
export function PortalPage() {
  const { user, isImpersonating, exitImpersonation, logout } = useAuth();
  const [profile, setProfile] = useState<PortalProfile | null>(null);
  const [quotes, setQuotes] = useState<PortalQuote[] | null>(null);
  const [invoices, setInvoices] = useState<PortalInvoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const [me, documents] = await Promise.all([getPortalMe(), getPortalDocuments()]);
      setProfile(me.portalUser);
      setQuotes(documents.quotes);
      setInvoices(documents.invoices);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Could not load the portal");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const respond = useCallback(
    async (quote: PortalQuote, action: "accept" | "reject"): Promise<void> => {
      if (busyId) return;
      setBusyId(quote.id);
      setError(null);
      try {
        await (action === "accept" ? portalAcceptQuote(quote.id) : portalRejectQuote(quote.id));
        await load();
      } catch (actionError) {
        setError(actionError instanceof Error ? actionError.message : "Action failed");
      } finally {
        setBusyId(null);
      }
    },
    [busyId, load],
  );

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{profile ? profile.clientName : "Portal"}</h1>
          <p className={styles.lede}>Quotes and invoices for your organisation.</p>
        </div>
        <div className={styles.headerSide}>
          <span className={styles.user}>{user?.email}</span>
          {isImpersonating ? (
            <button
              type="button"
              className={styles.exitImpersonation}
              onClick={() => void exitImpersonation()}
            >
              Exit to admin
            </button>
          ) : (
            <button type="button" className={styles.signOut} onClick={() => void logout()}>
              Sign out
            </button>
          )}
        </div>
      </header>

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Quotes</h2>
        {quotes === null ? (
          <p className={styles.note}>Loading…</p>
        ) : quotes.length === 0 ? (
          <p className={styles.note}>No quotes have been sent yet.</p>
        ) : (
          <ul className={styles.list}>
            {quotes.map((quote) => (
              <li key={quote.id} className={styles.row}>
                <span className={styles.docTitle}>
                  {quote.number} · v{quote.version}
                </span>
                <span className={`${styles.badge} ${quote.status === "accepted" ? styles.badgeAccepted : styles.badgeSent}`}>
                  {quote.status}
                </span>
                {quote.expiryDate ? <span className={styles.meta}>expires {quote.expiryDate}</span> : null}
                <span className={styles.total}>{formatPriceCents(quote.totalCents)}</span>
                <span className={styles.actions}>
                  <a className={styles.action} href={`/api/portal/quotes/${quote.id}/pdf`} target="_blank" rel="noreferrer">
                    PDF
                  </a>
                  {quote.status === "sent" && (
                    <>
                      <button
                        type="button"
                        className={styles.accept}
                        disabled={busyId === quote.id}
                        onClick={() => void respond(quote, "accept")}
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        className={styles.reject}
                        disabled={busyId === quote.id}
                        onClick={() => void respond(quote, "reject")}
                      >
                        Reject
                      </button>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Invoices</h2>
        {invoices === null ? (
          <p className={styles.note}>Loading…</p>
        ) : invoices.length === 0 ? (
          <p className={styles.note}>No invoices have been sent yet.</p>
        ) : (
          <ul className={styles.list}>
            {invoices.map((invoice) => (
              <li key={invoice.id} className={styles.row}>
                <span className={styles.docTitle}>{invoice.number}</span>
                <span className={`${styles.badge} ${invoice.status === "paid" ? styles.badgeAccepted : styles.badgeSent}`}>
                  {invoice.status}
                </span>
                {invoice.sentAt ? <span className={styles.meta}>sent {invoice.sentAt.slice(0, 10)}</span> : null}
                <span className={styles.total}>{formatPriceCents(invoice.totalCents)}</span>
                <span className={styles.actions}>
                  <a className={styles.action} href={`/api/portal/invoices/${invoice.id}/pdf`} target="_blank" rel="noreferrer">
                    PDF
                  </a>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
